import React from 'react';
import { Link, NavLink } from 'react-router-dom';

import { Box, Typography } from '@mui/material';

import styled from '@emotion/styled';
import dayjs from 'dayjs';
import { capitalize, upperCase } from 'lodash';

import { VenueSubscriptionItem } from '../../graphql/API';
import { Plan } from '../../pages/SubscriptionPlans/types';
import Button from '../Button/Button';
import { PlusIcon } from '../Dialogs/CollabsFilters/ToolTip';

type Props = {
  title?: string;
  totalReservations: number;
  outOfReservations: number;
  subscription?: VenueSubscriptionItem;
  subscriptionPlan?: Plan;
  showFullWidth?: boolean;
  toolTipComponent?: React.ReactNode;
};

export const BrandPromotionLimit: React.FC<Props> = ({
  title = 'Monthly reservations',
  totalReservations,
  outOfReservations,
  subscription,
  subscriptionPlan,
  showFullWidth = true,
  toolTipComponent,
}) => {
  return (
    <Container maxWidth={!showFullWidth ? '670px' : '100%'}>
      {subscriptionPlan ? (
        <>
          <Box display={'flex'} alignItems={'center'} gap={4.5}>
            <Limit>
              {totalReservations}
              {subscriptionPlan !== 'plus' ? `/${outOfReservations}` : ''}
            </Limit>
            <Box>
              <Box display={'flex'} alignItems={'center'} gap={1} sx={{ mb: 0.5 }}>
                {subscriptionPlan === 'plus' ? (
                  <Typography sx={{ fontSize: '18px', color: '#232832', fontWeight: 600 }}>
                    Reservation{totalReservations === 0 || totalReservations >= 2 ? 's' : ''} this
                    month
                  </Typography>
                ) : (
                  <Typography sx={{ fontSize: '18px', color: '#232832', fontWeight: 500 }}>
                    {totalReservations < outOfReservations
                      ? title
                      : 'You’ve hit your monthly promotion limit'}
                  </Typography>
                )}
                {toolTipComponent && toolTipComponent}
              </Box>

              <Typography sx={{ color: '#1A1C1E', fontSize: '15px' }}>
                {subscriptionPlan === 'plus' ? (
                  <>
                    Unlimited reservations included in your{' '}
                    <b>
                      <NavLink style={{ color: '#1A1C1E' }} to={'/plans'}>
                        {upperCase(subscriptionPlan)} Plan
                      </NavLink>
                    </b>
                    . Resets on {dayjs(subscription?.expirationDate).format('ll')}.
                  </>
                ) : (
                  <>
                    {totalReservations < outOfReservations ? (
                      <>
                        Included in your{' '}
                        <b>
                          <NavLink style={{ color: '#1A1C1E' }} to={'/plans'}>
                            {capitalize(subscriptionPlan)} Plan
                          </NavLink>
                        </b>
                        . Resets on {dayjs(subscription?.expirationDate).format('ll')}.
                      </>
                    ) : (
                      `Update your plan to unlock more promotions or wait ${dayjs(
                        subscription?.expirationDate,
                      ).format('ll')}`
                    )}
                  </>
                )}
              </Typography>
            </Box>
          </Box>
          {totalReservations >= outOfReservations && subscriptionPlan !== 'plus' && (
            <Button
              component={Link}
              variant='contained'
              color='primary'
              sx={{ fontSize: '18px', fontWeight: 500, px: 3, py: 1.3 }}
              startIcon={<PlusIcon sx={{ height: '16px', width: '16px' }} />}
              to='/plans'
            >
              Upgrade plan
            </Button>
          )}
        </>
      ) : (
        <>
          <Box display={'flex'} alignItems={'center'}>
            <Box>
              <Typography sx={{ fontSize: '18px', color: '#232832', fontWeight: 600 }}>
                Start promoting your brand
              </Typography>

              <Typography sx={{ color: '#1A1C1E', fontSize: '15px' }}>
                Upgrade to a plan to unlock Beautypass feature
              </Typography>
            </Box>
          </Box>
          <Button
            component={Link}
            variant='contained'
            color='primary'
            sx={{ fontSize: '18px', fontWeight: 500, px: 3, py: 1.3 }}
            startIcon={<PlusIcon sx={{ height: '16px', width: '16px' }} />}
            to='/plans'
          >
            Upgrade plan
          </Button>
        </>
      )}
    </Container>
  );
};

const Container = styled.div<{ maxWidth?: string }>`
  border-radius: 8px;
  border: 1px solid #f3f3f3;
  background-color: #fafafa;

  display: flex;
  align-items: center;
  justify-content: space-between;

  padding: 22px 28px;
  max-width: ${(props) => props.maxWidth || '100%'};
`;

const Limit = styled.h1`
  color: #232832;
  font-family: Poppins;
  font-size: 40px;
  font-weight: 600;
  margin: 0;
  padding: 0;
`;
